import { callEdgeFunction } from '../supabase.client';

interface ColumnRecord {
  schema: string;
  table: string;
  column: string;
  typeName: string;
}

interface IntrospectionState {
  columns: ColumnRecord[];
  typeValues: Record<string, string[]>; // `${schema}.${typeName}` → values
}

let state: IntrospectionState = { columns: [], typeValues: {} };
const inflight: Partial<Record<string, Promise<void>>> = {};

export async function refreshIntrospection(
  schema: 'dev' | 'staging' | 'production'
): Promise<void> {
  const existing = inflight[schema];
  if (existing) return existing;
  const p = (async () => {
    const types = await callEdgeFunction<{ name: string; values: string[] }[]>('enum-admin', {
      op: 'list_types',
      schema,
    });

    const newTypeValues: Record<string, string[]> = { ...state.typeValues };
    for (const t of types) newTypeValues[`${schema}.${t.name}`] = t.values;

    const otherColumns = state.columns.filter((c) => c.schema !== schema);
    const newColumns: ColumnRecord[] = [...otherColumns];
    for (const t of types) {
      const cols = await callEdgeFunction<{ table: string; column: string }[]>('enum-admin', {
        op: 'list_columns',
        schema,
        type_name: t.name,
      });
      for (const c of cols) {
        newColumns.push({ schema, table: c.table, column: c.column, typeName: t.name });
      }
    }
    state = { columns: newColumns, typeValues: newTypeValues };
  })().finally(() => {
    inflight[schema] = undefined;
  });
  inflight[schema] = p;
  return p;
}

export function isEnumColumn(schema: string, table: string, column: string): boolean {
  return state.columns.some((c) => c.schema === schema && c.table === table && c.column === column);
}

export function getEnumValues(schema: string, table: string, column: string): string[] | null {
  const col = state.columns.find(
    (c) => c.schema === schema && c.table === table && c.column === column
  );
  if (!col) return null;
  return state.typeValues[`${schema}.${col.typeName}`] ?? null;
}

// test-only
export function _setIntrospectionForTests(s: IntrospectionState): void {
  state = s;
}
